import React, {useMemo, useRef, useState} from 'react';
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {AgGridReact} from "ag-grid-react";
import StorageStatusActionCellRenderer from "./components/storage-status-action-cell-renderer.jsx";
import {useQuery} from "@tanstack/react-query";
import storageStatusService from "../../../services/web/storage-status-service.js";
import Button from "../../../components/button/button.jsx";
import StorageStatusAllDetails from "./components/storage-status-all-details.jsx";
import StorageStatusDetails from "./components/storage-status-details.jsx";
import "./css/storage-status.css"

const StorageStatus = () => {
    const [archiverSelected, setArchiverSelected] = useState(undefined);
    const [detailsModal, setDetailsModal] = useState(false);
    const [allDetailsModal, setAllDetailsModal] = useState(false);

    const {data, isLoading} = useQuery({
        queryFn: () => storageStatusService.status().then(({data}) => {
            const formatDate = (dtMin, dtMax, d, percent = false, plus = false) => {
                if (!dtMin || !dtMax) return "";

                const min = new Date(dtMin + " 00:00");
                const max = new Date(dtMax + " 00:00");

                let diffInDays = (max-min) / (24 * 60 * 60 * 1000);
                if (plus) diffInDays += 1;

                if (percent) {
                    const percent = (d.totalDiskUsedProtected / d.totalDisk) * 100;
                    const percentText = (d.totalDiskUsedProtected && d.totalDisk) ? `- ${percent.toFixed(1)}% do espaço` : "";

                    return `${min.toLocaleDateString()} a ${max.toLocaleDateString()} (${diffInDays} dias ${percentText})`;
                }

                return `${min.toLocaleDateString()} a ${max.toLocaleDateString()} (${diffInDays} dias)`;
            }

            return data.map(d => {
                d.totalDiskFormated = totalFormated(d.totalDisk);

                d.totalDiskUsed =  d.totalDiskUsed ? ((d.totalDiskUsed / d.totalDisk) * 100).toFixed() + "%" : ""

                d.withImage = formatDate(d.minDtWithImage, d.maxDtWithImage, d, false, true);
                d.withoutImage = formatDate(d.minDtWithoutImage, d.maxDtWithoutImage, d, false, d.minDtWithImage !== d.maxDtWithoutImage);
                d.protectImage = formatDate(d.minDtProtected, d.maxDtProtected,d, true);

                d.name = "Archiver " + d.archiverId;

                return d;
            });
        }),
        queryKey: ['status']
    });

    const {data: attaches} = useQuery({
        queryFn: () => storageStatusService.attaches(),
        queryKey: ['attaches'],
    });

    const gridRef = useRef(undefined);

    const [columnDefs, ] = useState([
        {headerName: "Servidor", field: "name", width: 90},
        {headerName: "Espaço total", field: "totalDiskFormated", width: 100},
        {headerName: "Utilizado",field: "totalDiskUsed", width: 100},
        {headerName: "Com imagens (Rotativo)", field: "withImage", flex: 1},
        {headerName: "Sem imagens (Expurgadas)", field: "withoutImage", flex: 1},
        {headerName: "Com imagens (Protegidas)", field: "protectImage", flex: 1},
        {
            headerName: "Detalhes",
            field: "",
            cellRenderer: StorageStatusActionCellRenderer,
            cellRendererParams: {setDetailsModal, setArchiverSelected},
            width: 80
        },
    ]);

    const defaultColDef = useMemo(
        () => ({
            resizable: false,
        }), []
    );

    const totalFormated = (total) => {
        if (!total) return "";

        if (total < 1024) return total + " B";

        const exp = Math.floor(Math.log(total) / Math.log(1024));
        const prefix = "kMGTPE".charAt(exp - 1);

        const formattedNumber = (total / Math.pow(1024, exp)).toFixed(1);
        return `${formattedNumber} ${prefix}B`
    }

    return (
        <>
            <Template isLoading={isLoading} className={"storage-template"}>
                <Template.Header className={"print:hidden"}>
                    <Template.Title>Status de armazenamento</Template.Title>
                </Template.Header>

                <Template.Body className={"space-y-3"}>
                    <Card className={"print:hidden"}>
                        <Card.Header>
                            <Card.Title>Passagens de veículos</Card.Title>
                        </Card.Header>
                        <Card.Body className={"space-y-3"}>
                            <AgGridReact
                                ref={gridRef}
                                rowHeight={40}
                                rowData={data ?? []}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                domLayout='autoHeight'
                            />

                            <Button className={"justify-self-end"} onClick={() => setAllDetailsModal(true)}>
                                Detalhes de todos Archivers
                            </Button>
                        </Card.Body>
                    </Card>
                    <Card className={"w-1/4 print:hidden"}>
                        <Card.Header>
                            <Card.Title>Anexos</Card.Title>
                        </Card.Header>
                        <Card.Body className={"grid grid-cols-12 gap-2"}>
                            <div className={"col-span-6"}>
                                <div className={"text-center my-4"}>
                                    <h3>Total</h3>
                                    <h3 className={"font-bold text-[16px]"}>
                                        {attaches?.total ?? ""}
                                    </h3>
                                </div>
                            </div>
                            <div className={"col-span-6"}>
                                <div className={"text-center my-4 border-l-2 border-l-[#e7eaec]"}>
                                    <h3>Espaço utilizado</h3>
                                    <h3 className={"font-bold text-[16px]"}>
                                        {attaches ? totalFormated(attaches.totalBytes) : ""}
                                    </h3>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <StorageStatusDetails
                        show={detailsModal}
                        archivers={data ?? []}
                        setShow={setDetailsModal}
                        archiverSelected={archiverSelected}
                    />

                    <StorageStatusAllDetails
                        show={allDetailsModal}
                        setShow={setAllDetailsModal}
                        archivers={data ?? []}
                    />
                </Template.Body>
            </Template>
        </>
    );
};

export default StorageStatus;